import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useLogMeetingNotes, type UpcomingMeeting } from "@/hooks/useCRM";

interface Props {
  open: boolean;
  onClose: () => void;
  meeting: UpcomingMeeting | null;
  companyId: string | undefined;
}

export default function LogMeetingNotesDialog({ open, onClose, meeting, companyId }: Props) {
  const [notes, setNotes] = useState("");
  const [direction, setDirection] = useState<"outbound" | "inbound">("outbound");
  const mutation = useLogMeetingNotes(companyId);

  useEffect(() => {
    if (open) {
      setNotes("");
      setDirection("outbound");
    }
  }, [open]);

  const submit = async () => {
    if (!meeting || !notes.trim()) return;
    try {
      await mutation.mutateAsync({
        followupId: meeting.id,
        notes,
        direction,
      });
      onClose();
    } catch {
      // Surface via mutation.error
    }
  };

  if (!meeting) return null;

  const startsDate = meeting.starts_at ? new Date(meeting.starts_at) : null;
  const errorDetail = mutation.error
    ? ((mutation.error as unknown as { response?: { data?: { detail?: string } } }).response?.data
        ?.detail ?? (mutation.error as Error).message)
    : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Log meeting notes</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorDetail && (
            <Alert severity="error" variant="outlined">
              {errorDetail}
            </Alert>
          )}

          <Box sx={{ p: 1.5, bgcolor: "#fafafa", borderRadius: 1 }}>
            <Typography variant="subtitle2">{meeting.summary}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {startsDate
                ? startsDate.toLocaleString(undefined, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </Typography>
            {meeting.attendee_emails.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                {meeting.attendee_emails.map((e) => (
                  <Chip key={e} label={e} size="small" variant="outlined" />
                ))}
              </Stack>
            )}
          </Box>

          <FormControl fullWidth>
            <InputLabel>Direction</InputLabel>
            <Select
              label="Direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value as "outbound" | "inbound")}
            >
              <MenuItem value="outbound">We hosted (outbound)</MenuItem>
              <MenuItem value="inbound">They called us (inbound)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="What was discussed / decided / next steps"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              "e.g. Walked through Q3 priorities. They want a follow-up proposal by " +
              "end of next week. Action: Steve to draft proposal, schedule review for Tue."
            }
            multiline
            minRows={6}
            fullWidth
            autoFocus
          />

          <Typography variant="caption" color="text.secondary">
            Creates a CRM communication entry linked to{" "}
            {meeting.individual_id ? "this contact + company" : "this company"} and marks the
            meeting as completed.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={mutation.isPending || !notes.trim()}>
          {mutation.isPending ? "Logging…" : "Log notes + mark done"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
